<script setup lang="ts">
import { NInput } from 'naive-ui'
import CvSectionEditor from './CvSectionEditor.vue'
import type { EditorEntry } from './cvEditorTypes'
import type { CvDocument } from '../stores/cv_versions'

// Structured editor for one draft. Every change emits a new document; the
// parent owns the copy being edited and decides when to save.
const props = defineProps<{ modelValue: CvDocument }>()
const emit = defineEmits<{ 'update:modelValue': [doc: CvDocument] }>()

type ListKey = 'approach' | 'roles' | 'board_roles' | 'projects' | 'education' | 'skills' | 'languages'

function setSummary(summary: string): void {
  emit('update:modelValue', { ...props.modelValue, summary })
}

// The section types are interfaces of string fields, which TypeScript won't
// treat as the editor's index-signature record without going through unknown.
function entriesOf(key: ListKey): EditorEntry[] {
  return props.modelValue[key] as unknown as EditorEntry[]
}

function setSection(key: ListKey, entries: EditorEntry[]): void {
  emit('update:modelValue', { ...props.modelValue, [key]: entries } as CvDocument)
}

const SECTIONS: Array<{
  key: ListKey
  title: string
  fields: Array<{ key: string; label: string; placeholder?: string }>
  listField?: { key: string; label: string; placeholder?: string }
  blank: () => EditorEntry
}> = [
  {
    key: 'approach',
    title: 'Leadership & approach',
    fields: [
      { key: 'heading', label: 'Trait', placeholder: 'Two to five words' },
      { key: 'detail', label: 'Evidence', placeholder: 'One sentence: a number, project, or outcome' },
    ],
    blank: () => ({ heading: '', detail: '' }),
  },
  {
    key: 'roles',
    title: 'Experience',
    fields: [
      { key: 'title', label: 'Title' },
      { key: 'employer', label: 'Employer' },
      { key: 'location', label: 'Location', placeholder: 'Optional' },
      { key: 'start', label: 'Start', placeholder: 'YYYY-MM or YYYY' },
      { key: 'end', label: 'End', placeholder: 'YYYY-MM, YYYY, or present' },
    ],
    listField: { key: 'bullets', label: 'Bullets, one per line' },
    blank: () => ({ title: '', employer: '', location: '', start: '', end: '', bullets: [] }),
  },
  {
    key: 'board_roles',
    title: 'Board positions',
    fields: [
      { key: 'title', label: 'Board role', placeholder: 'e.g. Chair of the board' },
      { key: 'organisation', label: 'Organisation' },
      { key: 'description', label: 'Description', placeholder: 'Type and size, optional' },
      { key: 'start', label: 'Start', placeholder: 'YYYY-MM or YYYY' },
      { key: 'end', label: 'End', placeholder: 'YYYY-MM, YYYY, or present' },
    ],
    listField: { key: 'bullets', label: 'Bullets, one per line' },
    blank: () => ({ title: '', organisation: '', description: '', start: '', end: '', bullets: [] }),
  },
  {
    key: 'projects',
    title: 'Projects',
    fields: [
      { key: 'name', label: 'Name' },
      { key: 'url', label: 'URL', placeholder: 'https://…, optional' },
      { key: 'period', label: 'Period', placeholder: 'YYYY or YYYY–YYYY' },
      { key: 'description', label: 'One-line description' },
    ],
    listField: { key: 'bullets', label: 'Bullets, one per line' },
    blank: () => ({ name: '', url: '', period: '', description: '', bullets: [] }),
  },
  {
    key: 'education',
    title: 'Education',
    fields: [
      { key: 'degree', label: 'Degree' },
      { key: 'institution', label: 'Institution' },
      { key: 'start', label: 'Start', placeholder: 'YYYY' },
      { key: 'end', label: 'End', placeholder: 'YYYY' },
    ],
    listField: { key: 'details', label: 'Details, one per line' },
    blank: () => ({ degree: '', institution: '', start: '', end: '', details: [] }),
  },
  {
    key: 'skills',
    title: 'Skills',
    fields: [{ key: 'category', label: 'Group' }],
    listField: { key: 'items', label: 'Skills, one per line' },
    blank: () => ({ category: '', items: [] }),
  },
  {
    key: 'languages',
    title: 'Languages',
    fields: [
      { key: 'language', label: 'Language' },
      { key: 'level', label: 'Level' },
    ],
    blank: () => ({ language: '', level: '' }),
  },
]
</script>

<template>
  <div class="flex flex-col gap-2.5" data-testid="cv-editor">
    <details class="group rounded-xl border border-border-subtle bg-surface-raised" open>
      <summary
        class="flex min-h-11 cursor-pointer list-none items-center gap-2 rounded-xl px-3 py-2.5 group-open:border-b group-open:border-border-divider [&::-webkit-details-marker]:hidden"
      >
        <span class="flex flex-1 flex-col gap-0.5">
          <span class="font-medium text-ink">Summary</span>
          <span class="text-xs text-ink-faint">1 paragraph</span>
        </span>
        <span class="text-ink-faint transition-transform group-open:rotate-90" aria-hidden="true">›</span>
      </summary>
      <label class="flex flex-col gap-1 p-3 text-xs text-ink-muted">
        <span class="sr-only">Summary</span>
        <NInput
          type="textarea"
          :autosize="{ minRows: 3 }"
          :value="modelValue.summary"
          data-testid="cv-edit-summary"
          @update:value="setSummary"
        />
      </label>
    </details>
    <CvSectionEditor
      v-for="section in SECTIONS"
      :key="section.key"
      :title="section.title"
      :testid="`cv-edit-${section.key}`"
      :entries="entriesOf(section.key)"
      :fields="section.fields"
      :list-field="section.listField"
      :blank="section.blank"
      @update:entries="(entries) => setSection(section.key, entries)"
    />
  </div>
</template>
